import React from 'react';
import IdentityLink from './IdentityLink';
import { formatDate, formatDecimal, formatDocument } from './contract';
import StageList from './StageList';

const missing = 'Não informado';
const noRows = [];
const place = (point) => [point?.city, point?.province].filter(Boolean).join(' - ') || missing;
const identifier = (value) => String(value ?? '').trim();
const definitions = {
  ATTENDED: {
    title: 'ATENDIDAS - VIAGENS',
    countDescription: 'viagens ordenadas pela data de coleta',
    loadingLabel: 'viagens atendidas',
  },
  IN_TRANSIT: {
    title: 'EM VIAGEM',
    countDescription: 'viagens em andamento',
    loadingLabel: 'viagens em andamento',
  },
  AT_DESTINATION: {
    title: 'NO DESTINO',
    countDescription: 'viagens no destino',
    loadingLabel: 'viagens no destino',
  },
};

// A API v1 fornece efetivações por ponto, não um evento global de início/fim da viagem.
// Preserva todos os pontos e suas ausências; chegada (arrivedAt) não significa início.
function completions(points) {
  if (!points?.length) return missing;
  return points
    .map(
      (point) => `${points.length > 1 ? `${place(point)}: ` : ''}${formatDate(point.completedAt)}`,
    )
    .join(' · ');
}

export default function TravelStageList({ context, rowIntegrations = {}, ...props }) {
  const code = context.group;
  const definition = definitions[code];
  if (!definition) return <p role="alert">Etapa de viagens não reconhecida.</p>;
  const {
    occurrences: Occurrences,
    driverContact: DriverContact,
    transitTime: TransitTime,
    transitTimeProvider: TransitTimeProvider,
  } = rowIntegrations;
  const integrationProps = (item) => ({ item, context, onChanged: props.onRefresh });
  const status = {
    key: 'status',
    label: 'Status',
    width: 195,
    value: (row) => row.status?.name || row.status?.code || missing,
  };
  const columns = [
    {
      key: 'position',
      label: '#',
      width: 44,
      resizable: false,
      value: (_, i) => String(i + 1).padStart(2, '0'),
    },
    {
      key: 'travel',
      label: 'Viagem',
      width: 115,
      value: (row) => String(row.travelId ?? row.id),
      render: (row, value) => (
        <IdentityLink
          to={`/viagens/${row.travelId ?? row.id}`}
          aria-label={`Abrir viagem ${row.travelId ?? row.id}`}
        >
          {value}
        </IdentityLink>
      ),
      addon: Occurrences ? (row) => <Occurrences {...integrationProps(row)} /> : undefined,
    },
    {
      key: 'reference',
      label: 'Viagem Benner',
      width: 175,
      value: (row) => identifier(row.externalLoadId) || identifier(row.travelNumber) || missing,
    },
    // Vínculo não é fornecido por KanbanPresenter v1. Não inferir pela placa/transportador.
    ...(code !== 'ATTENDED'
      ? [{ key: 'link', label: 'Vínculo', width: 160, value: () => missing }]
      : []),
    // Em Atendidas, Status vem antes de Cliente; nas demais, perto do fim.
    ...(code === 'ATTENDED' ? [status] : []),
    {
      key: 'client',
      label: 'Cliente',
      width: 220,
      value: (row) =>
        `${row.client?.name || 'Cliente não informado'} · ${formatDocument(row.client?.cnpj)}`,
    },
    {
      key: 'carrier',
      label: 'Transportador',
      width: 200,
      value: (row) => row.carrier?.name || missing,
    },
    {
      key: 'driver',
      label: 'Motorista',
      width: 180,
      value: (row) => row.driver?.name || missing,
      addon: DriverContact ? (row) => <DriverContact {...integrationProps(row)} /> : undefined,
    },
    {
      key: 'vehicle',
      label: 'Veículo',
      width: 200,
      value: (row) =>
        [
          row.vehicle?.plate || missing,
          ...(row.wagons || []).map((wagon) => wagon.plate || missing),
        ].join(' / '),
    },
    { key: 'origin', label: 'Origem', width: 165, value: (row) => place(row.origin) },
    { key: 'destination', label: 'Destino', width: 165, value: (row) => place(row.destination) },
    ...(code === 'ATTENDED'
      ? [
          {
            key: 'pickup',
            label: 'Coleta Agendada',
            width: 155,
            value: (row) => formatDate(row.scheduledPickupAt),
          },
          {
            key: 'delivery',
            label: 'Entrega Agendada',
            width: 165,
            value: (row) => formatDate(row.scheduledDeliveryAt),
          },
          {
            key: 'loadingStarted',
            label: 'Carregamento Iniciado',
            width: 185,
            value: () => missing,
          },
          {
            key: 'loadingFinished',
            label: 'Carregamento Finalizado',
            width: 195,
            value: (row) => completions(row.origins),
          },
        ]
      : code === 'IN_TRANSIT'
        ? [
            {
              key: 'loadingFinished',
              label: 'Carregamento Finalizado',
              width: 195,
              value: (row) => completions(row.origins),
            },
            {
              key: 'delivery',
              label: 'Entrega Agendada',
              width: 165,
              value: (row) => formatDate(row.scheduledDeliveryAt),
            },
          ]
        : [
            {
              key: 'delivery',
              label: 'Entrega Agendada',
              width: 165,
              value: (row) => formatDate(row.scheduledDeliveryAt),
            },
            {
              key: 'unloading',
              label: 'Descarregamento Iniciado | Finalizado',
              width: 330,
              value: (row) => `${missing} | ${completions(row.destinations)}`,
            },
          ]),
    {
      key: 'product',
      label: 'Produto',
      width: 175,
      value: (row) =>
        [
          row.product?.name || missing,
          row.quantity?.weight != null ? `${formatDecimal(row.quantity.weight)} kg` : null,
          row.quantity?.volume != null ? `Volume: ${row.quantity.volume}` : null,
          row.quantity?.pallets != null ? `Pallets: ${row.quantity.pallets}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
    },
    {
      key: 'tracking',
      label: 'Rastreado',
      width: 150,
      value: (row) =>
        [
          row.tracking?.required === true
            ? 'Sim'
            : row.tracking?.required === false
              ? 'Não'
              : missing,
          row.tracking?.tracker?.name,
        ]
          .filter(Boolean)
          .join(' · '),
    },
    ...(code === 'ATTENDED' ? [] : [status]),
    { key: 'updated', label: 'Atualizado', width: 160, value: (row) => formatDate(row.updatedAt) },
  ];
  const list = (
    <StageList
      {...props}
      context={context}
      key={code}
      columns={columns}
      definition={{
        ...definition,
        code,
        className: 'tc-k-travel-list',
        tableLabel: `Lista ${definition.loadingLabel}`,
        emptyMessage: 'Nenhuma viagem nesta etapa.',
      }}
      renderRowDetails={
        TransitTime && code !== 'ATTENDED'
          ? (row) => <TransitTime {...integrationProps(row)} />
          : undefined
      }
    />
  );
  return TransitTimeProvider && code !== 'ATTENDED' ? (
    <TransitTimeProvider context={context} rows={props.queryState.groups?.[code]?.data || noRows}>
      {list}
    </TransitTimeProvider>
  ) : (
    list
  );
}
